import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, AlertOctagon, CalendarClock, TrendingUp, ArrowRight, Inbox, Coffee, Layout, Star, Pin, Bell } from 'lucide-react'
import { useProject } from './ProjectLayout'
import { NewStoryDialog } from './NewStoryDialog'
import { Button, Card, Chip, statusTone, priorityTone, cn } from '../../design'
import { useAutoQuery } from '../../hooks/useAutoQuery'
import { getDashboard, type StorySummary, type ActivityItem, type DashboardData, type TodayEvents } from '../../api/dashboard'
import { useEventAlarms } from '../../hooks/useEventAlarms'

/**
 * Phase 4 — Dashboard V1 (per `wf-dashboard.jsx` V1 — stat cards + my work + activity).
 *
 * Layout:
 *   [stat cards × 4]
 *   [ my work (1.4fr)  |  sprint progress + activity (1fr) ]
 */
export default function Dashboard() {
  const project = useProject()
  const [focus, setFocus] = useState(false)
  const [creating, setCreating] = useState(false)
  const navigate = useNavigate()
  const dq = useAutoQuery({
    queryKey: ['dashboard', project.id],
    queryFn: () => getDashboard(project.id),
  })

  // Dashboard relies on TanStack 10s polling (useAutoQuery default) for refresh.
  // SSE intentionally not subscribed here so we keep open SSE-connection count
  // low — each connection holds one PHP-FPM worker on DreamHost shared hosting,
  // and saturating workers across views starves regular API requests.
  // Board + StoryDrawer remain SSE-subscribed for snappier drag/edit echo.

  // Schedule browser-notification alarms for today's events + scheduled stories.
  const alarmItems = useMemo(() => {
    const t = dq.data?.today_events
    if (!t) return []
    return [
      ...t.events.map(e => ({ id: `ev:${e.id}`, title: e.title, start_at: e.start_at })),
      ...t.stories.map(s => ({ id: `st:${s.id}`, title: `${s.key} — ${s.title}`, start_at: s.scheduled_start })),
    ]
  }, [dq.data])
  useEventAlarms(alarmItems)

  if (focus && dq.data) {
    return <FocusMode data={dq.data} onExit={() => setFocus(false)} />
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-6 space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-base font-semibold tracking-tight">Project Dashboard</h2>
          <p className="text-xs text-fg-subtle">Snapshot of work in flight, sprint progress, and recent activity.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="md"
            variant="outline"
            leftIcon={<Coffee size={14} />}
            onClick={() => setFocus(true)}
            disabled={!dq.data}
            title="Focus mode (Dashboard V3 — desk metaphor)"
          >
            Focus mode
          </Button>
          <Button size="md" variant="outline">Filters</Button>
          <Button size="md" leftIcon={<Plus size={14} />} onClick={() => setCreating(true)}>New story</Button>
          <NewStoryDialog
            projectId={project.id}
            open={creating}
            onOpenChange={setCreating}
            onCreated={story => navigate(`/projects/${project.key}/backlog?story=${story.key}`)}
          />
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="In sprint"
          value={dq.data ? dq.data.stats.in_sprint.total : '—'}
          sub={
            dq.data
              ? `${dq.data.stats.in_sprint.done} done · ${dq.data.stats.in_sprint.doing} doing · ${dq.data.stats.in_sprint.todo} todo`
              : 'no active sprint'
          }
          accent="info"
        />
        <StatCard
          label="Blocked"
          value={dq.data?.stats.blocked ?? '—'}
          sub={dq.data && dq.data.stats.blocked > 0 ? 'open blockers' : 'nothing blocked'}
          icon={<AlertOctagon size={14} />}
          accent="danger"
        />
        <StatCard
          label="This week"
          value={dq.data?.stats.this_week ?? '—'}
          sub="due in next 7 days"
          icon={<CalendarClock size={14} />}
          accent="warning"
        />
        <StatCard
          label="Velocity"
          value={dq.data?.stats.velocity ?? '—'}
          sub={dq.data?.stats.velocity != null ? 'last closed sprint pts' : 'no closed sprint yet'}
          icon={<TrendingUp size={14} />}
          accent="success"
        />
      </div>

      {/* Two-column body */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5">
        <div className="flex flex-col gap-5">
          <TodayPanel today={dq.data?.today_events} loading={dq.isLoading} />
          <MyWorkPanel
            items={dq.data?.my_work ?? []}
            loading={dq.isLoading}
            projectKey={project.key}
          />
        </div>
        <div className="flex flex-col gap-5">
          <SprintProgressCard data={dq.data?.active_sprint ?? null} loading={dq.isLoading} />
          <ActivityCard items={dq.data?.activity ?? []} loading={dq.isLoading} projectKey={project.key} />
        </div>
      </div>
    </div>
  )
}

// ───────────────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  icon,
  accent,
}: {
  label: string
  value: number | string
  sub?: string
  icon?: React.ReactNode
  accent?: 'info' | 'danger' | 'warning' | 'success'
}) {
  const accentBar = {
    info:    'bg-info',
    danger:  'bg-danger',
    warning: 'bg-warning',
    success: 'bg-success',
  } as const
  return (
    <Card padded={false} className="overflow-hidden">
      <div className={cn('h-1', accent && accentBar[accent])} />
      <div className="px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-fg-subtle font-medium">{label}</div>
          {icon && <span className="text-fg-subtle">{icon}</span>}
        </div>
        <div className="text-3xl font-semibold tabular-nums tracking-tight mt-1">{value}</div>
        {sub && <div className="text-xs text-fg-subtle mt-1.5">{sub}</div>}
      </div>
    </Card>
  )
}

function TodayPanel({ today, loading }: { today: TodayEvents | undefined; loading: boolean }) {
  const items = useMemo(() => {
    if (!today) return []
    const a: Array<{
      kind: 'event' | 'story'
      id: number
      title: string
      sub?: string | null
      color?: string | null
      start: Date
      end: Date | null
    }> = []
    for (const e of today.events) {
      a.push({
        kind: 'event',
        id: e.id,
        title: e.title,
        sub: e.type,
        color: e.color,
        start: new Date(e.start_at.replace(' ', 'T')),
        end: e.end_at ? new Date(e.end_at.replace(' ', 'T')) : null,
      })
    }
    for (const s of today.stories) {
      a.push({
        kind: 'story',
        id: s.id,
        title: `${s.key} — ${s.title}`,
        sub: s.status_category,
        color: s.epic_color,
        start: new Date(s.scheduled_start.replace(' ', 'T')),
        end: s.scheduled_end ? new Date(s.scheduled_end.replace(' ', 'T')) : null,
      })
    }
    a.sort((x, y) => x.start.getTime() - y.start.getTime())
    return a
  }, [today])

  const todayDay = useMemo(() => new Date(), [])
  const sameDayItems = items.filter(i => sameDay(i.start, todayDay))
  const tomorrowItems = items.filter(i => !sameDay(i.start, todayDay))

  return (
    <Card padded={false}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <h3 className="text-base font-semibold tracking-tight flex items-center gap-2">
            <Bell size={14} className="text-accent" /> Today
          </h3>
          <p className="text-xs text-fg-subtle mt-0.5">
            {loading ? '…' : `${sameDayItems.length} today · ${tomorrowItems.length} tomorrow`}
          </p>
        </div>
        <Link to="../calendar" className="text-xs text-accent hover:underline">Calendar →</Link>
      </div>
      <div className="divide-y divide-border">
        {sameDayItems.length === 0 && tomorrowItems.length === 0 && !loading && (
          <div className="px-5 py-6 text-xs text-fg-subtle italic text-center">
            Nothing scheduled. Open the calendar to add something.
          </div>
        )}
        {sameDayItems.map((it, i) => <TodayRow key={`t-${i}`} item={it} day="today" />)}
        {tomorrowItems.length > 0 && (
          <div className="px-5 py-1.5 text-[10px] uppercase tracking-wider text-fg-subtle bg-bg-muted">
            Tomorrow
          </div>
        )}
        {tomorrowItems.map((it, i) => <TodayRow key={`m-${i}`} item={it} day="tomorrow" />)}
      </div>
    </Card>
  )
}

function TodayRow({ item, day }: {
  item: { kind: 'event' | 'story'; title: string; sub?: string | null; color?: string | null; start: Date; end: Date | null }
  day: 'today' | 'tomorrow'
}) {
  const ring = item.color ?? (item.kind === 'event' ? '#d97757' : '#7e57c2')
  const now = new Date()
  const upcoming = day === 'today' && item.start.getTime() > now.getTime()
  const minsUntil = Math.round((item.start.getTime() - now.getTime()) / 60_000)
  return (
    <div className="px-5 py-3 flex items-start gap-3">
      <div className="flex-shrink-0 w-1 self-stretch rounded-full" style={{ backgroundColor: ring }} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{item.title}</div>
        <div className="text-[11px] text-fg-subtle font-mono uppercase tracking-wider mt-0.5">
          {item.kind} · {item.sub ?? ''}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-xs font-mono text-fg-muted">
          {fmtTimeShort(item.start)}{item.end && `–${fmtTimeShort(item.end)}`}
        </div>
        {upcoming && minsUntil <= 90 && (
          <div className="text-[10px] text-accent font-medium mt-0.5">
            in {minsUntil < 1 ? 'now' : minsUntil + 'm'}
          </div>
        )}
      </div>
    </div>
  )
}

function fmtTimeShort(d: Date): string {
  const h12 = ((d.getHours() + 11) % 12) + 1
  const m = d.getMinutes()
  const ampm = d.getHours() >= 12 ? 'p' : 'a'
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function MyWorkPanel({
  items,
  loading,
  projectKey,
}: {
  items: StorySummary[]
  loading: boolean
  projectKey: string
}) {
  return (
    <Card padded={false}>
      <div className="flex items-baseline justify-between px-5 py-4 border-b border-border">
        <div>
          <h3 className="text-base font-semibold tracking-tight">My work</h3>
          <p className="text-xs text-fg-subtle mt-0.5">
            {loading ? '…' : `${items.length} assigned to you`}
          </p>
        </div>
        <Link
          to={`/projects/${projectKey}/backlog`}
          className="text-xs text-fg-subtle hover:text-fg flex items-center gap-1"
        >
          View backlog <ArrowRight size={12} />
        </Link>
      </div>

      {loading && <div className="px-5 py-4 text-sm text-fg-subtle">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="px-5 py-10 text-center">
          <Inbox size={28} className="mx-auto text-fg-disabled" />
          <p className="text-sm font-medium text-fg-muted mt-2">Nothing assigned to you</p>
          <p className="text-xs text-fg-subtle mt-1">
            Assign yourself stories from the backlog to see them here.
          </p>
        </div>
      )}

      <ul className="divide-y divide-border">
        {items.map(s => (
          <li key={s.id} className="px-5 py-2.5 flex items-center gap-3 hover:bg-bg-muted/50 group">
            <span className="font-mono text-xs text-fg-subtle w-16 shrink-0">{s.key}</span>
            <span className="text-sm text-fg flex-1 truncate">{s.title}</span>
            {s.epic_name && (
              <Chip
                tone={s.epic_color ? 'epic' : 'neutral'}
                color={s.epic_color ?? undefined}
                size="sm"
                dot
              >
                {s.epic_name}
              </Chip>
            )}
            <Chip size="sm" tone={priorityTone(s.priority)}>
              {s.priority}
            </Chip>
            <Chip size="sm" tone={statusTone(s.status_category)}>
              {s.status_name}
            </Chip>
          </li>
        ))}
      </ul>
    </Card>
  )
}

function SprintProgressCard({
  data,
  loading,
}: {
  data: import('../../api/dashboard').ActiveSprint | null
  loading: boolean
}) {
  if (loading) {
    return (
      <Card>
        <h3 className="text-sm font-semibold">Active sprint</h3>
        <p className="text-xs text-fg-subtle mt-2">Loading…</p>
      </Card>
    )
  }
  if (!data) {
    return (
      <Card>
        <h3 className="text-sm font-semibold">No active sprint</h3>
        <p className="text-xs text-fg-subtle mt-2">Start a sprint to track progress here.</p>
      </Card>
    )
  }
  const pct = Math.max(0, Math.min(100, data.percent_done))
  return (
    <Card>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold tracking-tight">{data.name}</h3>
        <span className="text-xs text-fg-subtle font-mono">{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-bg-muted overflow-hidden">
        <div
          className="h-full bg-accent transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-fg-subtle mt-3">
        <span>{fmtDate(data.start_date)}</span>
        <span className="font-mono">
          {data.done_points} / {data.total_points} pts
        </span>
        <span>{fmtDate(data.end_date)}</span>
      </div>
      {data.goal && (
        <p className="text-xs text-fg-muted mt-3 line-clamp-2 italic">"{data.goal}"</p>
      )}
    </Card>
  )
}

function ActivityCard({
  items,
  loading,
  projectKey,
}: {
  items: ActivityItem[]
  loading: boolean
  projectKey: string
}) {
  return (
    <Card padded={false} className="flex-1 min-h-0 flex flex-col">
      <div className="px-5 py-4 border-b border-border">
        <h3 className="text-sm font-semibold tracking-tight">Activity</h3>
        <p className="text-xs text-fg-subtle mt-0.5">Recent project events</p>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading && <p className="px-5 py-3 text-sm text-fg-subtle">Loading…</p>}
        {!loading && items.length === 0 && (
          <p className="px-5 py-3 text-sm text-fg-subtle">No recent activity.</p>
        )}
        <ul className="divide-y divide-border">
          {items.map((a, i) => (
            <li key={i} className="px-5 py-2.5">
              <div className="flex items-start gap-2 text-sm">
                <span className="text-[10px] font-mono text-fg-subtle pt-0.5 w-12 shrink-0">
                  {timeAgo(a.when)}
                </span>
                <span className="flex-1">
                  <span className="text-fg-muted">{a.actor ?? 'Someone'}</span>{' '}
                  <ActivityVerb kind={a.kind} />{' '}
                  <Link
                    to={`/projects/${projectKey}/backlog?story=${a.story.key}`}
                    className="text-accent-hover hover:underline font-mono text-xs"
                  >
                    {a.story.key}
                  </Link>{' '}
                  <span className="text-fg-muted">"{a.story.title}"</span>
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  )
}

function ActivityVerb({ kind }: { kind: string }) {
  if (kind === 'created')   return <span className="text-fg-subtle">created</span>
  if (kind === 'completed') return <span className="text-success font-medium">completed</span>
  if (kind === 'updated')   return <span className="text-fg-subtle">updated</span>
  if (kind.startsWith('status_changed_to_')) {
    const cat = kind.replace('status_changed_to_', '').replace('_', ' ')
    return <span className="text-info">moved to <em className="not-italic font-medium">{cat}</em></span>
  }
  return <span className="text-fg-subtle">{kind}</span>
}

function fmtDate(d: string | null): string {
  if (!d) return '—'
  const date = new Date(d + (d.length === 10 ? 'T00:00:00' : ''))
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function timeAgo(ts: string): string {
  const t = new Date(ts.replace(' ', 'T')).getTime()
  const diff = Date.now() - t
  const m = Math.floor(diff / 60_000)
  if (m < 1) return 'now'
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  return `${d}d`
}

// ─── Dashboard V3 — Focus mode (desk metaphor) ────────────────────────────

function FocusMode({ data, onExit }: { data: DashboardData; onExit: () => void }) {
  const top = data.my_work[0]
  const upNext = data.my_work.slice(1, 5)
  const sprint = data.active_sprint
  const today = data.today_events
  const todayItems = useMemo(() => {
    if (!today) return [] as Array<{ title: string; start: Date; sub: string }>
    const merged = [
      ...today.events.map(e => ({ title: e.title, start: new Date(e.start_at.replace(' ', 'T')), sub: e.type })),
      ...today.stories.map(s => ({ title: `${s.key} ${s.title}`, start: new Date(s.scheduled_start.replace(' ', 'T')), sub: 'story' })),
    ]
    merged.sort((a, b) => a.start.getTime() - b.start.getTime())
    const now = new Date()
    return merged.filter(i => i.start.getTime() > now.getTime() - 30 * 60_000).slice(0, 4)
  }, [today])

  return (
    <div className="min-h-full p-8" style={{ backgroundColor: 'var(--color-bg-muted)' }}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-baseline gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">The desk.</h1>
          <span className="text-xs text-fg-subtle italic">work as physical objects · arrangement = priority</span>
        </div>
        <Button variant="outline" size="md" leftIcon={<Layout size={14} />} onClick={onExit}>
          Exit focus mode
        </Button>
      </div>

      <div className="relative" style={{ minHeight: 600 }}>
        {/* Pinned focus card (top-left, slight tilt) */}
        {top ? (
          <PaperCard
            className="absolute top-0 left-0 w-[320px] p-5"
            tilt={-1.5}
          >
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-accent font-bold mb-2">
              <Star size={12} fill="currentColor" /> Focus today
            </div>
            <h2 className="text-xl font-semibold tracking-tight leading-tight mb-2">{top.title}</h2>
            <div className="flex items-center gap-2 mb-3">
              <Chip size="sm" tone={priorityTone(top.priority)}>{top.priority}</Chip>
              {top.epic_name && (
                <Chip size="sm" tone="epic" color={top.epic_color ?? '#9aa0a6'} dot>
                  {top.epic_name}
                </Chip>
              )}
              {top.estimate_points !== null && (
                <Chip size="sm" tone="neutral" className="font-mono">{top.estimate_points} pt</Chip>
              )}
            </div>
            {top.due_date && (
              <p className="text-xs text-fg-muted">
                due <span className="font-medium">{new Date(top.due_date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
              </p>
            )}
          </PaperCard>
        ) : (
          <PaperCard className="absolute top-0 left-0 w-[320px] p-5" tilt={-1.5}>
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-accent font-bold mb-2">
              <Star size={12} fill="currentColor" /> Focus today
            </div>
            <p className="text-sm text-fg-muted">Nothing assigned. Take a breath.</p>
          </PaperCard>
        )}

        {/* Sticky notes */}
        <StickyNote
          className="absolute"
          style={{ top: 60, left: 360, transform: 'rotate(2.5deg)' }}
          color="#fff7b8"
        >
          <span className="text-[10px] uppercase text-fg-subtle">{sprint?.name ?? 'sprint'}</span>
          <div>
            {sprint
              ? `${sprint.percent_done}% done · ${sprint.done_points}/${sprint.total_points} pt`
              : 'no active sprint'}
          </div>
        </StickyNote>

        <StickyNote
          className="absolute"
          style={{ top: 180, left: 380, transform: 'rotate(-2.8deg)' }}
          color="#c8e6c9"
        >
          <span className="text-[10px] uppercase text-fg-subtle">stats</span>
          <div>
            {data.stats.in_sprint.total} in sprint<br />
            {data.stats.blocked} blocked<br />
            {data.stats.this_week} this week
          </div>
        </StickyNote>

        {/* Up next stack (right side, rotated cards) */}
        <div className="absolute" style={{ top: 20, right: 30, width: 320 }}>
          <h3 className="text-base font-semibold tracking-tight text-fg-muted mb-3 ml-1">
            Up next ▾
          </h3>
          <div className="space-y-3">
            {upNext.length === 0 && (
              <p className="text-xs text-fg-subtle italic">Nothing else queued for you.</p>
            )}
            {upNext.map((s, i) => (
              <PaperCard
                key={s.id}
                className="px-4 py-3"
                tilt={i % 2 === 0 ? -0.4 : 0.6}
              >
                <div className="flex items-center justify-between text-[10px] font-mono text-fg-subtle mb-1">
                  <span>{s.key}</span>
                  {s.estimate_points !== null && <span>{s.estimate_points} pt</span>}
                </div>
                <div className="text-sm leading-snug">{s.title}</div>
                {s.epic_name && (
                  <div className="text-[10px] text-fg-subtle mt-1">{s.epic_name}</div>
                )}
              </PaperCard>
            ))}
          </div>
        </div>

        {/* Today's events strip (paper card, low-left of center) */}
        <PaperCard
          className="absolute p-4 w-[320px]"
          style={{ top: 320, left: 60 }}
          tilt={-0.6}
        >
          <h3 className="text-sm font-semibold tracking-tight mb-2 flex items-center gap-1.5">
            <Bell size={12} className="text-accent" /> Today
          </h3>
          {todayItems.length === 0 ? (
            <p className="text-xs text-fg-subtle italic">Nothing scheduled.</p>
          ) : (
            <ul className="space-y-1.5">
              {todayItems.map((it, i) => (
                <li key={i} className="flex items-baseline gap-2 text-xs">
                  <span className="font-mono text-fg-subtle w-12 shrink-0">
                    {it.start.getHours() % 12 === 0 ? 12 : it.start.getHours() % 12}
                    {it.start.getMinutes() === 0 ? '' : ':' + String(it.start.getMinutes()).padStart(2, '0')}
                    {it.start.getHours() >= 12 ? 'p' : 'a'}
                  </span>
                  <span className="flex-1 truncate">{it.title}</span>
                </li>
              ))}
            </ul>
          )}
        </PaperCard>

        {/* Recent-activity peek (bottom-left, slight tilt) */}
        <PaperCard
          className="absolute p-4 w-[420px]"
          style={{ bottom: 40, left: 50 }}
          tilt={0.7}
        >
          <h3 className="text-sm font-semibold tracking-tight mb-3 flex items-center gap-1.5">
            <Pin size={12} /> Recent activity
          </h3>
          <ul className="space-y-1.5">
            {data.activity.slice(0, 4).map((a, i) => (
              <li key={i} className="text-xs">
                <span className="font-mono text-fg-subtle mr-2">{timeAgo(a.when)}</span>
                <span className="text-fg-muted">{a.actor ?? 'Someone'}</span>
                <span className="text-fg-subtle"> · </span>
                <span className="font-mono text-accent-hover">{a.story.key}</span>
                <span className="text-fg-muted"> {a.kind.replace('status_changed_to_', 'moved to ').replace('_', ' ')}</span>
              </li>
            ))}
          </ul>
        </PaperCard>

        {/* Blockers pile (bottom-right) */}
        {data.stats.blocked > 0 && (
          <PaperCard
            className="absolute p-4 w-[260px]"
            style={{ bottom: 50, right: 50 }}
            tilt={-1.2}
          >
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-danger font-bold mb-2">
              <AlertOctagon size={12} /> Blockers
            </div>
            <p className="text-2xl font-semibold tabular-nums">{data.stats.blocked}</p>
            <p className="text-xs text-fg-muted mt-1">
              stories waiting on a blocking dependency
            </p>
          </PaperCard>
        )}
      </div>
    </div>
  )
}

function PaperCard({
  children,
  className,
  style,
  tilt = 0,
}: {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  tilt?: number
}) {
  return (
    <div
      className={cn('rounded-md border border-border bg-bg-elevated', className)}
      style={{
        transform: `rotate(${tilt}deg)`,
        boxShadow: '3px 4px 0 rgba(0,0,0,0.05), 0 8px 16px rgba(0,0,0,0.08)',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

function StickyNote({
  children,
  className,
  style,
  color = '#fff7b8',
}: {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  color?: string
}) {
  return (
    <div
      className={cn('px-3 py-2 text-sm leading-snug w-[150px]', className)}
      style={{
        backgroundColor: color,
        boxShadow: '2px 3px 0 rgba(0,0,0,0.12)',
        ...style,
      }}
    >
      {children}
    </div>
  )
}
